import { useMatches } from "@tanstack/react-router";
import { CheckSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSelectionMode } from "@/components/records/selection-mode/selection-mode-provider";
import { useOnlineStatus } from "@/offline/online-status";

interface ProjectHeaderIssuesActionProps {
  /** `report:Create` — a report is the one thing issues are selected for. */
  canCreateReport: boolean;
}

/**
 * Right-action slot content for [[project-header.spec]] on the Issues list:
 * the Select button that starts picking issues for a report.
 *
 * Renders only on the issues list route (not an issue's own page), online, and
 * for holders of `report:Create`. While selecting it renders nothing — every
 * selection on Issues is report mode, whose only Cancel is the bulk bar's. See
 * [[selection-mode.spec]] US-008.
 */
export function ProjectHeaderIssuesAction({
  canCreateReport,
}: ProjectHeaderIssuesActionProps) {
  const matches = useMatches();
  const currentPath = matches[matches.length - 1]?.fullPath ?? "";
  // The list's template is exactly this path; `/issues/$issueId` is not.
  const isOnIssuesList = currentPath === "/projects/$projectId/issues/";
  if (!isOnIssuesList || !canCreateReport) return null;

  return <IssuesActions />;
}

/** Split out so the hooks below only run on the issues list. */
function IssuesActions() {
  const { isSelectionMode, enterSelectionMode } = useSelectionMode();
  const isOnline = useOnlineStatus();

  // Selection mode is unavailable offline, and issues don't load offline
  // either — no dead control. See offline-availability.md.
  if (isSelectionMode || !isOnline) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => enterSelectionMode()}
      aria-label="Enter selection mode"
    >
      <CheckSquare className="h-4 w-4" />
      Select
    </Button>
  );
}
